import { useState, useRef, useEffect } from 'react';
import { AgentMetricsData } from '../../agent-performance/agentTableTypes';

interface UseStickyRowProps {
  data: AgentMetricsData[];
}

export const useStickyRow = ({ data }: UseStickyRowProps) => {
  const [stickyRowIndex, setStickyRowIndex] = useState<number | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Handle scroll to determine which main row should be sticky  
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      const scrollTop = scrollContainer.scrollTop;
      const rowHeight = 45; // Height of each row
      
      // Find which row should be sticky based on scroll position
      let currentStickyIndex = null;
      let accumulatedHeight = 0; // Start from 0 since we're measuring from scroll container top

      for (let i = 0; i < data.length; i++) {
        const rowTop = accumulatedHeight;
        
        // Check if this row's main row is in the sticky zone
        if (data[i].isExpanded) {
          const expandedHeight = data[i].details ? data[i].details.length * rowHeight : rowHeight;
          const totalRowHeight = rowHeight + expandedHeight;
          
          // If the scroll position is within this expanded section
          if (scrollTop >= rowTop && scrollTop < rowTop + totalRowHeight) {
            // But only if the main row is scrolled past the top
            if (scrollTop > rowTop) {
              currentStickyIndex = i;
            }
            break;
          }
          
          accumulatedHeight += totalRowHeight;
        } else {
          accumulatedHeight += rowHeight;
        }
      }

      setStickyRowIndex(currentStickyIndex);
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    // Initial call to set sticky state
    handleScroll();
    
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [data]);

  return {
    stickyRowIndex,
    scrollContainerRef,
  };
};